import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DashboardPageLayout } from '@/components/layouts/dashboard-page-layout'
import { TabContainer } from '@/components/organisms/accounts-receivable-manager/tab-container'
import { css } from '@emotion/core'
import { exportAccountsReceivableBalanceForManager, fetchAccountsReceivableBalanceForManager } from '@/apis/aipass'
import { AccountsReceivableBalance } from '@/models/accounts-receivable-manager'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { LoaderContextCreator } from '@/contexts/loader'
import dayjs from 'dayjs'
import uuid from 'react-uuid'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { useDayjsRange } from '@/hooks/use-dayjs-range'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { AccountContext } from '@/contexts/account'
import { AggregatePeriodModal } from '@/components/organisms/accounts-receivable-manager/aggregate-period-modal'
import { TypeLabel } from '@/components/organisms/accounts-receivable-manager/type-label'

export const AccountsReceivableManagerBalance: React.FC = () => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { account, fetchAdminAccount } = useContext<any>(AccountContext)
  const { errorHandler } = useErrorHandler()
  const { dayjsRange } = useDayjsRange()

  const [searchResponse, setSearchResponse] = useState<{
    indicator: { prevBalancePrice: number; accountsReceivablePrice: number; currentReceiptPrice: number; currentBalancePrice: number }
    results: AccountsReceivableBalance[]
  }>()
  const [salesDate, setSalesDate] = useState(dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'))
  const [isOpenAggregatePeriodModal, setIsOpenAggregatePeriodModal] = useState(false)

  const viewMonthRange = dayjsRange({
    start: dayjs().startOf('month').subtract(5, 'month'),
    end: dayjs().endOf('month'),
    interval: 1,
    unit: 'month',
  }).reverse()

  const doSearch = async (searchCondition: { salesDate: string }) => {
    try {
      setIsLoading(true)
      setSearchResponse(await fetchAccountsReceivableBalanceForManager(searchCondition))
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const doExport = async () => {
    try {
      setIsLoading(true)
      const { url } = await exportAccountsReceivableBalanceForManager({ salesDate })
      window.open(url, '_blank')
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    doSearch({ salesDate })
  }, [salesDate])

  return (
    <DashboardPageLayout title={t('A/R Management')}>
      <TabContainer currentTab="A/R Balance">
        <div className="headline-block">
          <div
            css={css({
              display: 'flex',
              alignItems: 'center',
              fontSize: 14,
              color: 'rgb(39, 39, 39)',
              gap: 8,
            })}
          >
            <div>{t('Sales date')}</div>
            <Select customStyle={css({ width: 150, paddingBottom: 0 })} value={salesDate} onChange={v => setSalesDate(v.toString())}>
              {viewMonthRange.map(d => (
                <AntSelect.Option key={uuid()} value={d.format('YYYY-MM-DD')}>
                  {d.format(t('YYYY-MM'))}
                </AntSelect.Option>
              ))}
            </Select>

            <div
              css={css({
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                paddingLeft: 8,
                span: { color: '#f2a40b', padding: '0 4px' },
              })}
            >
              <div>
                売掛金の計算開始月:<span>{dayjs(account.hotel.accountsReceivableAggregatePeriod).format('YYYY年MM月')}</span>から計算
              </div>
              <Button
                width={140}
                height={32}
                buttonType={ButtonType.Primary}
                onClick={() => setIsOpenAggregatePeriodModal(true)}
                fontSize={12}
              >
                計算開始月を設定
              </Button>
            </div>
          </div>
          <div>
            <Button
              buttonType={ButtonType.NonStyle}
              width={170}
              height={32}
              fontSize={12}
              icon={require('@/static/images/csv.svg')}
              onClick={doExport}
            >
              {t('Export CSV')}
            </Button>
          </div>
        </div>

        <div className="indicator-block">
          <div>
            <div className="sub-title">{t('A/R Balance from prev month')}</div>
            <div className="title">¥{searchResponse?.indicator.prevBalancePrice.toLocaleString() || '-'}</div>
          </div>
          <div>
            <div className="sub-title">{t('A/R for current month')}</div>
            <div className="title">¥{searchResponse?.indicator.accountsReceivablePrice.toLocaleString() || '-'}</div>
          </div>
          <div>
            <div className="sub-title">{t('Deposit for current month')}</div>
            <div className="title">¥{searchResponse?.indicator.currentReceiptPrice.toLocaleString() || '-'}</div>
          </div>
          <div>
            <div className="sub-title">{t('A/R Balance for current month')}</div>
            <div className="title">¥{searchResponse?.indicator.currentBalancePrice.toLocaleString() || '-'}</div>
          </div>
        </div>

        <div className="table-block">
          {searchResponse?.results.length !== 0 ? (
            <div css={tableStyle}>
              <div className="table-header">
                <div className="table-row">
                  <div className="table-cell col-ar">{t('Accounts receivable')}</div>
                  <div className="table-cell col-type">{t('Type')}</div>
                  <div className="table-cell col-price">{t('A/R Balance from prev month')}</div>
                  <div className="table-cell col-price">{t('A/R for current month')}</div>
                  <div className="table-cell col-price">{t('Deposit for current month')}</div>
                  <div className="table-cell col-price">{t('A/R Balance for current month')}</div>
                </div>
              </div>
              <div className="table-body">
                {searchResponse?.results.map(responseRow => (
                  <div className="table-row" key={uuid()}>
                    <div className="table-cell col-ar">{responseRow.customerName}</div>
                    <div className="table-cell col-type">
                      <TypeLabel isDiscount={responseRow.isDiscount} />
                    </div>
                    <div className="table-cell col-price">¥{responseRow.prevBalancePrice.toLocaleString()}</div>
                    <div className="table-cell col-price">¥{responseRow.accountsReceivablePrice.toLocaleString()}</div>
                    <div className="table-cell col-price">¥{responseRow.currentReceiptPrice.toLocaleString()}</div>
                    <div className="table-cell col-price">¥{responseRow.currentBalancePrice.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-table">
              <h1>{t('No data available')}</h1>
            </div>
          )}
        </div>

        {isOpenAggregatePeriodModal && (
          <AggregatePeriodModal
            value={account.hotel.accountsReceivableAggregatePeriod}
            onCancel={() => setIsOpenAggregatePeriodModal(false)}
            onSave={() => {
              setIsOpenAggregatePeriodModal(false)
              fetchAdminAccount()
              doSearch({ salesDate })
            }}
          />
        )}
      </TabContainer>
    </DashboardPageLayout>
  )
}

const tableStyle = css({
  '.col-ar': {
    minWidth: 'calc((100% - 8px - 60px - 100px - 100px - 100px - 100px - (16px * 6)))',
    maxWidth: 'calc((100% - 8px - 60px - 100px - 100px - 100px - 100px - (16px * 6)))',
  },
  '.col-type': { minWidth: 60, maxWidth: 60 },
  '.col-price': { minWidth: 100, maxWidth: 100, textAlign: 'right' },
})
